import React, { Component } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import Header from '../../../shared/Header/Header';
import Logout from '../../../shared/Logout/Logout';
import imprivataIcon from '../Icons/imprivata.svg';
import face from '../Icons/face.svg';
import './IcpFace.scss';
import IcpFaceErrorBanner from '../../../shared/IcpFaceErrorBanner/IcpFaceErrorBanner';

/**
 * IcpFace prompt for login
 * @class IcpFace
 * @extends {Component}
 */
class IcpFace extends Component {
  errorTimeoutId = null;
  errorBannerDuration = 10000; // 10 seconds
  state = {
    Authn: null,
    EventProvider: null,
    NotificationSystem: null,
    ErrorCode: null,
    EventType: null,
    FatalError: null,
    IGNORED_ERROR_CODES: [],
    IGNORED_ERROR_MESSAGES: [],
    AUTO_REDIRECT_ERROR_CODES: [],
    FACTOR_LOCK_ERROR_CODES: [],
    icpError: null,
    INFO_CODES: null,
    WARNING_CODES: null,
    ERROR_CODES: null,
    DEFAULT_BANNER_STATUS: null,
    isAuthenticated: false,
    icpInitialized: false,
    faceRecognitionError: false,
  };
  static propTypes = {
    /** Header title */
    headerTitle: PropTypes.string,
    /** Function to execute when form is submitted. */
    onSubmit: PropTypes.func,
    /** Is something loading? */
    isLoading: PropTypes.bool,
    /** A username used for facial authentication */
    username: PropTypes.string,
    /** A Icp tenant id */
    icpTenantId: PropTypes.string,
    /** Use Parkland custom UX */
    useCustomUX: PropTypes.bool,
    /** The component to be used for logout */
    logoutComponent: PropTypes.object,
    /** link to display more options */
    moreOptionsLink: PropTypes.object,
    /** Function to perform logout. */
    logout: PropTypes.func,
    /** Function to show CAUI error. */
    showCauiErrorBanner: PropTypes.func,
    /** Called when no camera device is detected. */
    onNoCameraDevice: PropTypes.func,
  };
  static defaultProps = {
    isLoading: false,
    headerTitle: 'Log in',
    username: '',
    icpTenantId: '',
    onSubmit: () => void (0),
    // Parkland custom UX
    useCustomUX: false,
    logoutComponent: <Logout />,
  };

  async componentDidMount() {
    const [
      {
        Authn,
        ErrorCode,
        ErrorMessage,
        EventType,
        FatalError,
        INFO_CODES,
        WARNING_CODES,
        ERROR_CODES,
      },
    ] = await Promise.all([import('@imprivata-cloud/authn'),]);
    const [ { DEFAULT_BANNER_STATUS, }, ] =
      await Promise.all([import('@imprivata-cloud/components'),]);
    const [ { EventProvider, }, ] =
      await Promise.all([import('@imprivata-cloud/core'),]);
    const [ { NotificationSystem, }, ] =
      await Promise.all([import('@imprivata-cloud/design-system'),]);
    const AUTO_REDIRECT_ERROR_CODES = [
      ErrorCode.INVALID_CREDENTIALS,
      ErrorCode.FACTOR_CAPTURE_FAILED,
      ErrorCode.FACE_TIMEOUT,
    ];
    const FACTOR_LOCK_ERROR_CODES = [
      ErrorCode.FACTOR_LOCK_PERSISTENT,
      ErrorCode.FACTOR_LOCK_TEMPORARY,
    ];
    const IGNORED_ERROR_CODES = [
      ErrorCode.LOW_QUALITY,
      ErrorCode.NO_FACE_DETECTED,
      ErrorCode.FACE_LIMIT,
      ErrorCode.DUPLICATE,
      ErrorCode.LIVENESS_FAILED,
    ];
    const IGNORED_ERROR_MESSAGES = [
      ErrorMessage.INCORRECT_TENANT_ID,
      ErrorMessage.NOT_ENROLLED_CARD,
    ];
    this.setState({
      Authn,
      EventProvider,
      NotificationSystem,
      ErrorCode,
      EventType,
      FatalError,
      AUTO_REDIRECT_ERROR_CODES,
      FACTOR_LOCK_ERROR_CODES,
      IGNORED_ERROR_CODES,
      IGNORED_ERROR_MESSAGES,
      INFO_CODES,
      WARNING_CODES,
      ERROR_CODES,
      DEFAULT_BANNER_STATUS,
    });
  }

  componentWillUnmount() {
    clearTimeout(this.errorTimeoutId);
    this.errorTimeoutId = null;
  }

  onEvent = (e) => {
    const {
      EventType, ErrorCode, isAuthenticated, AUTO_REDIRECT_ERROR_CODES, FACTOR_LOCK_ERROR_CODES,
    } = this.state;
    console.log('Event', e);
    switch (e.event) {
    case EventType.INITIALIZED:
      this.setState({ icpInitialized: true, });
      break;
    case EventType.START_AUTHENTICATING:
      break;
    case EventType.AUTHENTICATED:
      if (!isAuthenticated) {
        this.setState({ isAuthenticated: true, });
        this.props.onSubmit(e.data?.oidcAuthorizationCode ?? '');
      }
      break;
    case EventType.ERROR:
      if (e.data?.code && AUTO_REDIRECT_ERROR_CODES.includes(e.data.code) ) {
        this.props.onSubmit('');
      }
      else if (e.data?.code && FACTOR_LOCK_ERROR_CODES.includes(e.data.code) ) {
        if (this.props.onNoCameraDevice) {
          const switchToAnotherModality = false;
          this.props.onNoCameraDevice(switchToAnotherModality);
          this.props.onSubmit('');
        }
      }
      else {
        this.displayFaceRecognitionErrorBanner(e.data);
      }
      break;
    case EventType.ONLINE:
    case EventType.RESTART_STATE:
      this.clearErrorTimeout();
      break;
    case EventType.OFFLINE:
      this.displayIcpErrorBanner({ code: ErrorCode.OFFLINE, });
      break;
    case EventType.IMPR_ID_ERROR:
    case EventType.INTERNAL_ERROR:
      if (e.data) {
        if (typeof e.data === 'string') {
          this.displayIcpErrorBanner(
            {
              message: e.data,
              code: ErrorCode.UNKNOWN,
            }
          );
        }
        else {
          this.displayIcpErrorBanner(e.data);
        }
      }
      break;
    case EventType.PERMISSION_REVOKED:
    case EventType.CAMERA_ERROR:
      if (e.data) {
        if (e.data.code === ErrorCode.NO_CAMERA_DEVICE && this.props.onNoCameraDevice) {
          const switchToAnotherModality = true;
          this.props.onNoCameraDevice(switchToAnotherModality);
          break;
        }
        this.displayIcpErrorBanner(e.data);
      }
      break;
    case EventType.DEVICE_DISCONNECTED:
      if (e.data) {
        if (this.props.onNoCameraDevice) {
          const switchToAnotherModality = true;
          this.props.onNoCameraDevice(switchToAnotherModality);
        }
        else if (typeof e.data === 'string') {
          this.displayIcpErrorBanner(
            {
              message: e.data,
              code: ErrorCode.DEVICE_DISCONNECTED,
            }
          );
        }
        else {
          this.displayIcpErrorBanner(e.data);
        }
      }
      break;
    case EventType.PERMISSION_GRANTED:
    case EventType.DEVICE_RECONNECTED:
      this.clearErrorTimeout();
      break;
    default:
      console.log('Unhandled event: ', e);
      break;
    }
  };

  displayFaceRecognitionErrorBanner = (error) => {
    this.displayStatusBanner(error, () => {
      this.props.onSubmit('');
    });
    this.setState({ faceRecognitionError: true, });
  };

  displayIcpErrorBanner = (error) => {
    const { logout, showCauiErrorBanner, } = this.props;
    this.displayStatusBanner(error, () => {
      logout();
      showCauiErrorBanner();
    });
  };

  displayStatusBanner = (error, onTimeout) => {
    const {
      FatalError, IGNORED_ERROR_CODES, IGNORED_ERROR_MESSAGES,
    } = this.state;
    const noBanner =
      error instanceof FatalError ||
      (error.code && IGNORED_ERROR_CODES.includes(error.code) ) ||
      (error.message && IGNORED_ERROR_MESSAGES.includes(error.message) );
    if (noBanner) {
      this.setErrorTimeout( () => {
        onTimeout();
      }, null);
      return;
    }

    const errorMessages = require('./ErrorMessages.json');
    const newBanner = {
      message: error.code ? errorMessages[error.code] : error.message,
      status: this.mapErrorToStatus(error),
      subMessage: errorMessages[`${error.code}-sub-message`],
    };

    this.setErrorTimeout( () => {
      onTimeout();
    }, newBanner);
  };

  mapErrorToStatus = (errorCode) => {
    const {
      INFO_CODES, WARNING_CODES, ERROR_CODES, DEFAULT_BANNER_STATUS,
    } = this.state;
    const { code, } = errorCode;
    if (!code) {
      return DEFAULT_BANNER_STATUS;
    }
    if (INFO_CODES.includes(code) ) {
      return 'info';
    }
    if (WARNING_CODES.includes(code) ) {
      return 'warning';
    }
    if (ERROR_CODES.includes(code) ) {
      return 'error';
    }
    return DEFAULT_BANNER_STATUS;
  };

  setErrorTimeout = (handler, icpError) => {
    if (!this.state.faceRecognitionError) {
      this.clearErrorTimeout();
      this.errorTimeoutId = setTimeout(handler, this.errorBannerDuration);
      this.setState({ icpError: icpError, });
    }
  };

  clearErrorTimeout = () => {
    if (!this.state.faceRecognitionError) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
      this.setState({ icpError: null, });
    }
  };

  render() {
    const {
      Authn, EventProvider, NotificationSystem, icpError, icpInitialized, faceRecognitionError,
    } = this.state;
    const {
      username, logoutComponent, useCustomUX, headerTitle, icpTenantId, moreOptionsLink,
    } = this.props;
    const logoutStyle = useCustomUX ? 'black' : 'white';

    const contextResource = {
      'resourceType': 'impr-eam-endpoint',
      'contextType': 'idp-authn',
    };
    const tracing = { workflowId: '', };
    const factors = [{ 'factorType': 'face', },];
    const deviceFactors = [
      'face',
      'pwd',
    ];
    const requestConfig = {
      'clientName': 'idp-web',
      'username': username,
      'oidcRequestData': {
        'codeChallenge': '',
        'codeChallengeMethod': '',
      },
      'requestHeaders': { 'createSession': { 'Impr-Session-Id': '', }, },
    };
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    });

    const featureFlags = {
      disableIAD: true,
      fitVideoFrame: true,
    };

    const showFaceOptionLink = moreOptionsLink && icpInitialized;
    return (
      <div className='CIDRA'>
        {!faceRecognitionError
          ? logoutComponent
          : (<Logout type={logoutStyle} onLogoutClick={() => this.props.onSubmit('')} />)
        }
        {useCustomUX ?
          (<Header />)
          :
          (
            <div className='Header-Box'>
              <img src={face} className='Modality-Icon' alt='face' />
              <div className='Header-Text'>
                <span>{headerTitle}</span>
              </div>
            </div>
          )
        }
        <div>
          {(Authn && EventProvider && NotificationSystem) && (<>
            <div className='authn-container'>
              {icpError ? (<IcpFaceErrorBanner showDuration = {this.errorBannerDuration} icpError={icpError}/>) : null}
              <QueryClientProvider client={queryClient}>
                <EventProvider>
                  <NotificationSystem>
                    <Authn
                      contextResource={contextResource}
                      tenantId={icpTenantId}
                      factors={factors}
                      deviceFactors={deviceFactors}
                      onEvent={this.onEvent}
                      requestConfig={requestConfig}
                      tracing={tracing}
                      featureFlags={featureFlags}
                      hideFactorSwitch={true}
                      checkFactorState={false}
                      allowChangeUsername={false} />
                  </NotificationSystem>
                </EventProvider>
              </QueryClientProvider>
            </div>
            {(showFaceOptionLink && !faceRecognitionError) && (<div className='More-Options-Face'>{moreOptionsLink}</div>)}
          </>
          )}
        </div>
        <hr className='Footer-hr' />
        <img className='Footer' src={imprivataIcon} alt='logo' />
      </div>
    );
  }
}

export default IcpFace;
